import React, { Component } from 'react';
import { Clients, ErrorHandler, checkIsVerified } from '../api';
import { colors } from '../styles/utils';
import { history } from '../store';

import { isAuthorized, isUpsell, FEATURE_LIST } from '../permissions';
import { UpsellCard } from '../components/UpsellCard/UpsellCard';

const networkThreats = `${process.env.REACT_APP_CDN_URL}/assets/images/portalupsell_networkthreats_bg.jpg`;
const internetUtilization = `${process.env.REACT_APP_CDN_URL}/assets/images/portalupsell_internetutilization_bg.jpg`;
const emailSecurity = `${process.env.REACT_APP_CDN_URL}/assets/images/portalupsell_emailsecurity_bg.jpg`;
const dnsActivity = `${process.env.REACT_APP_CDN_URL}/assets/images/portalupsell_dnsactivity_bg.jpg`;

const featureData = [
  {
    feature: FEATURE_LIST.DNS_ACTIVITY,
    title: 'DNS Activity',
    dashboardText:
      'Symplexity Secure Clients can search the web with confidence, knowing that their internet searches are being routed to reputable sites.',
    detailsText: (
      <p>
        Symplexity Secure Clients can search the web with confidence, knowing
        that their internet searches are being routed to reputable sites.
        <br />
        <br />
        DNS Activity is available to all our Symplexity Secure Clients, however,
        this service is not available with your current contract.
      </p>
    ),
    backgroundImg: dnsActivity,
  },
  {
    feature: FEATURE_LIST.NETWORK_THREATS,
    title: 'Threat Detection & Remediation',
    dashboardText:
      'Symplexity can identify the source of your threats and take action to protect your organization. Learn More',
    detailsText: (
      <p>
        The perimeter of your network is under constant attack from malicious
        sources. Symplexity’s Security Operation Center uses advanced threat
        analytics to filter out the noise and identify meaningful actions that
        are needed in order to secure your environment.
        <br />
        <br />
        Threat Detection & Remediation is available to all our Symplexity Secure
        Clients, however, these are not available with your current contract.
      </p>
    ),
    backgroundImg: networkThreats,
  },
  {
    feature: FEATURE_LIST.INTERNET_UTILIZATION,
    title: 'Internet Utilization Monitoring',
    dashboardText:
      'Symplexity tracks internet utilization so that you can determine when the source of your threats and take action to stay protected. Learn More',
    detailsText: (
      <p>
        Gain visibility into your internet utilization to uncover the root cause
        behind bottle-necks in your bandwidth. This information is critical when
        making cost-benefit decisions about any adjustments with your Internet
        Service Provider.
        <br />
        <br />
        Internet Utilization Monitoring is available to all our Symplexity
        Secure Clients, however, these are not available with your current
        contract.
      </p>
    ),
    backgroundImg: internetUtilization,
  },
  {
    feature: FEATURE_LIST.SECURITY_AWARENESS_TRAINING,
    title: 'Automated Phishing Tests and Security Awareness Training',
    dashboardText:
      'Do you know who your most risky email users are? We can tell you. Learn More',
    detailsText: (
      <p>
        Knowledge is power when it comes to email security. Automated Phishing
        Tests administered by Symplexity help expose your most risky users.
        Coupled with ongoing Security Training, you you can turn your biggest
        weakness into your strongest line of defense.
        <br />
        <br />
        Automated Phishing Tests and Security Awareness Training is available to
        all our Symplexity Secure Clients, however, these are not available with
        your current contract.
      </p>
    ),
    backgroundImg: emailSecurity,
  },
];

const upsellFeatureLookup = feature =>
  featureData.find(f => f.feature === feature);

// HOC for checking auth for components.
// Expects a string with the name of the feature to check auth for.
export const Authorization = feature => {
  return WrappedComponent => {
    class WithAuthorization extends Component {
      _isMounted = false;
      constructor(props) {
        const featureDataItem = upsellFeatureLookup(feature);
        const { backgroundImg, title, detailsText } = featureDataItem || {};
        super(props);
        this.state = {
          isLoading: false,
          backgroundImg,
          title,
          detailsText,
          isComplete: false,
        };
      }
      componentDidMount() {
        // make sure they are fully logged in before we do anything, else, kick em out to `/login`
        this.checkIsFullyLoggedIn();
        this._isMounted = true;
      }
      componentWillUnmount() {
        this._isMounted = false;
      }
      safeSetState = (newState, callback) => {
        if (this._isMounted) {
          this.setState(newState, callback);
        }
      };
      handleRequestFeature = () => {
        const successText = (
          <p>
            Thank you for your inquiry. Someone from our team will contact you
            shortly.
          </p>
        );
        this.safeSetState({ isLoading: true });

        Clients.requestFeature({ featureName: feature })
          .then(() => {
            this.safeSetState({
              isLoading: false,
              detailsText: successText,
              isComplete: true,
            });
          })
          .catch(
            err =>
              this.safeSetState({ isLoading: false }, () =>
                ErrorHandler.bug(err)
              ) // use bug here just so we can keep a record if this fails
          );
      };

      checkIsFullyLoggedIn = () => {
        if (!checkIsVerified()) {
          // if they are not verified, then the either dont have a token, or their token is 'basic', either way
          // send them back to `/login` to sort it out
          history.push('/login');
        }
      };

      render() {
        const {
          isLoading,
          isComplete,
          backgroundImg,
          title,
          detailsText,
        } = this.state;

        if (isAuthorized(feature)) {
          return <WrappedComponent {...this.props} />;
        } else if (isUpsell(feature) && upsellFeatureLookup(feature)) {
          return (
            <div
              style={{
                background: `url(${backgroundImg}) 0px 0px / cover, ${colors.upsellBackground}`,
                margin: '-1.5rem',
                height: 'calc(100vh - 60px)', // full height minus the height of the topbar so we dont get scrolling
                overflow: 'hidden',
              }}>
              <UpsellCard
                title={title}
                body={detailsText}
                linkTitle={'Contact me about adding this feature'}
                color={colors.black}
                onClick={this.handleRequestFeature}
                isLoading={isLoading}
                isComplete={isComplete}
              />
            </div>
          );
        } else {
          return (
            <div
              className="well"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
              }}>
              <h3>You don't have permission to view this.</h3>
              <p>
                If you feel this is incorrect, please contact your
                administrator.
              </p>
            </div>
          );
        }
      }
    }

    return WithAuthorization;
  };
};

export default Authorization;
